import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  ChipProps,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IEsignEnvelopeEmailSend } from "../../../../../models/esign-envelope-email-send";
import { IForubusSignatureLogData } from "../../../../../models/forubus-signature-log";
import { getEsignEnvelopeEmailSend } from "../../../../../services/esignEnvelopeEmailSend.service";
import { ForubusSignatureFieldsRow } from "./ForubusSignatureFieldsRow";
import { ForubusSignatureSection } from "./ForubusSignatureSection";

interface IEsignEnvelopeEmailSendModalProps {
  data: IForubusSignatureLogData;
  onClose: () => void;
}

type TViewState = "loading" | "loaded" | "notSent" | "error";

const translationPath = "4uESign.page.logs.forubusSignatureLogs.emailSend.";

const getStatusTranslationKey = (status: string) => {
  switch (status) {
    case "PENDING":
      return "pending";
    case "SENDING":
      return "sending";
    case "SENT":
      return "sent";
    case "ERROR":
      return "error";
    default:
      return "unknown";
  }
};

const getStatusColor = (status: string): ChipProps["color"] => {
  switch (status) {
    case "SENT":
      return "success";
    case "ERROR":
      return "error";
    case "SENDING":
      return "info";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
};

const formatSentAt = (value: string, locale: Locale) => {
  const date = new Date(value);

  if (isNaN(date.getTime())) return "—";

  return format(date, "Pp", { locale }).replaceAll(",", "");
};

const EsignEnvelopeEmailSendModal: React.FC<
  IEsignEnvelopeEmailSendModalProps
> = ({ data, onClose }) => {
  const theme = useTheme();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [emailSend, setEmailSend] = useState<IEsignEnvelopeEmailSend | null>(
    null,
  );
  const [viewState, setViewState] = useState<TViewState>("loading");
  const [requestVersion, setRequestVersion] = useState(0);

  const locale = language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es;

  useEffect(() => {
    let isActive = true;

    const loadEmailSend = async () => {
      setViewState("loading");
      setEmailSend(null);

      try {
        const response = await getEsignEnvelopeEmailSend(
          data.documentCode,
          data.firstIntegrationId,
        );

        if (!isActive) return;

        if (!response.data) {
          setViewState("notSent");
          return;
        }

        setEmailSend(response.data);
        setViewState("loaded");
      } catch (error) {
        if (!isActive) return;

        if (axios.isAxiosError(error) && error.response?.status === 404) {
          setViewState("notSent");
          return;
        }

        setViewState("error");
      }
    };

    loadEmailSend();

    return () => {
      isActive = false;
    };
  }, [data.documentCode, data.firstIntegrationId, requestVersion]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="email-send-modal-title"
      PaperProps={{
        sx: {
          borderRadius: 1,
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          background: `linear-gradient(135deg,
            ${alpha(theme.palette.primary.main, 0.14)} 0%,
            ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmailOutlinedIcon
              sx={{ color: theme.palette.primary.main, fontSize: 20 }}
            />
            <Typography
              id="email-send-modal-title"
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "1rem" }}
            >
              {t(translationPath + "title")}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: "block",
              color: theme.palette.text.secondary,
            }}
          >
            {t(translationPath + "identification", {
              documentCode: data.documentCode,
              encounterNumber: data.firstIntegrationId,
            })}
          </Typography>
        </Box>

        <Tooltip title={t(translationPath + "close")}>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t(translationPath + "close") || ""}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent sx={{ px: 3, py: 3, minHeight: 240 }}>
        {viewState === "loading" && (
          <Box
            sx={{
              minHeight: 190,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress size={30} />
            <Typography variant="body2" color="text.secondary">
              {t(translationPath + "loading")}
            </Typography>
          </Box>
        )}

        {viewState === "notSent" && (
          <Alert severity="info" variant="outlined">
            <AlertTitle>{t(translationPath + "not_sent")}</AlertTitle>
            {t(translationPath + "not_sent_description")}
          </Alert>
        )}

        {viewState === "error" && (
          <Alert
            severity="error"
            variant="outlined"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => setRequestVersion((current) => current + 1)}
              >
                {t(translationPath + "retry")}
              </Button>
            }
          >
            <AlertTitle>{t(translationPath + "load_error")}</AlertTitle>
            {t(translationPath + "load_error_description")}
          </Alert>
        )}

        {viewState === "loaded" && emailSend && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 1,
              }}
            >
              <Typography variant="subtitle2">
                {t(translationPath + "status")}
              </Typography>
              <Chip
                size="small"
                color={getStatusColor(emailSend.statusSend)}
                label={t(
                  translationPath +
                    "statusValues." +
                    getStatusTranslationKey(emailSend.statusSend),
                )}
                sx={{ fontWeight: 700 }}
              />
            </Box>

            <Divider />

            <ForubusSignatureSection
              title={t(translationPath + "send_details")}
              icon={<EmailOutlinedIcon sx={{ fontSize: 14 }} />}
            >
              <ForubusSignatureFieldsRow
                icon={<PersonOutlineIcon fontSize="small" />}
                label={t(translationPath + "recipient")}
                value={emailSend.mailTo}
              />
              <ForubusSignatureFieldsRow
                icon={<EventOutlinedIcon fontSize="small" />}
                label={t(translationPath + "sent_at")}
                value={
                  emailSend.sentAt
                    ? formatSentAt(emailSend.sentAt, locale)
                    : t(translationPath + "not_sent_yet")
                }
              />
              <ForubusSignatureFieldsRow
                icon={<AttachFileOutlinedIcon fontSize="small" />}
                label={t(translationPath + "attachments_count")}
                value={emailSend.attachmentsCount.toString()}
              />
              <ForubusSignatureFieldsRow
                icon={<ReplayIcon fontSize="small" />}
                label={t(translationPath + "attempts")}
                value={emailSend.retries.toString()}
              />
              <ForubusSignatureFieldsRow
                icon={<ErrorOutlineIcon fontSize="small" />}
                label={t(translationPath + "error_message")}
                value={
                  emailSend.statusMessage ||
                  t(translationPath + "no_error_message")
                }
              />
            </ForubusSignatureSection>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="outlined" onClick={onClose}>
          {t(translationPath + "close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EsignEnvelopeEmailSendModal;
